import { faTrashAlt } from "@fortawesome/free-solid-svg-icons/faTrashAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import graphql from "babel-plugin-relay/macro";
import classNames from "classnames";
import styles from "components/phonenumber/PhoneNumber.module.scss";
import { accountMessages } from "domain/account/accountMessages";
import { isPhoneNumberType, phoneNumberTypes } from "domain/account/models/phoneNumber";
import { PhoneNumber_configuration, PhoneNumber_configuration$key } from "generatedQueries/PhoneNumber_configuration.graphql";
import React, { useMemo } from "react";
import { FieldArrayPath, FieldValues, useController, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";
import { useFragment } from "react-relay";
import { OnChangeValue } from "react-select";
import MaskedInput, { conformToMask, Mask } from "react-text-mask";
import { FormFeedback, Input } from "reactstrap";
import Button from "reactstrap/lib/Button";
import { ISelectOption, isSelectOption, ReactSelect } from "shared/components/ReactSelect";
import { mapMessages } from "shared/mapMessages";
import { useTranslatedValidations } from "shared/validations/useTranslatedValidations";
import { combine, isPhoneNumber, maxLength, minLength, required } from "shared/validations/validations";

const PLACEHOLDER_CHARACTER = "_";

const messages = mapMessages("components.phonenumber.PhoneNumber", {
    phoneNumberInputLabel: "Phone Number {index}",
    phoneNumberTypeInputLabel: "Phone Number {index} Type",
    phoneNumberTypePlaceholderLabel: "Type",
    removePhoneNumberTooltip: "Remove Phone Number {index}",
});

type Props<TFieldValues extends FieldValues> = {
    configurationRef: PhoneNumber_configuration$key;
    formFieldArrayName: FieldArrayPath<TFieldValues>;
    index: number;
    shouldRenderRemoveButton?: boolean;
    onRemoveClicked?: () => void;
};

export const PhoneNumber = <TFieldValues extends FieldValues>({
    configurationRef,
    formFieldArrayName,
    index,
    shouldRenderRemoveButton,
    onRemoveClicked,
}: Props<TFieldValues>) => {
    const configuration = useFragment(fragment, configurationRef);

    const { formatMessage } = useIntl();
    const mask = useMemo(() => getMask(configuration), [configuration]);
    const selectOptions = useMemo<ISelectOption[]>(() => {
        return phoneNumberTypes.map(phoneNumberType => ({
            ...phoneNumberType,
            label: formatMessage(phoneNumberType.label),
        }));
    }, [formatMessage]);

    const { trigger } = useFormContext();

    const typeRules = useTranslatedValidations(required(accountMessages.phoneTypeMissingError));
    const {
        field: typeField,
        fieldState: { isTouched: typeIsTouched, invalid: typeIsInvalid, error: typeError }
    } = useController({
        name: `${formFieldArrayName}.${index}.type`,
        rules: typeRules,
    });

    const numberRules = useTranslatedValidations(combine(required(), minLength(10), maxLength(20), isPhoneNumber(formatMessage)));
    const {
        field: numberField,
        fieldState: { isTouched: numberIsTouched, invalid: numberIsInvalid, error: numberError }
    } = useController({
        name: `${formFieldArrayName}.${index}.number`,
        rules: numberRules,
    });

    const errorElementId = `${formFieldArrayName}.${index}-error`;
    const error = typeError ?? numberError;

    return <div className="form-group">
        <div className="input-group">
            <div className={classNames(
                "input-group-prepend form-select",
                (typeIsTouched || typeIsInvalid) && ["was-validated", typeIsInvalid ? "invalid" : "valid"]
            )}>
                <ReactSelect
                    aria-label={formatMessage(messages.phoneNumberTypeInputLabel, { index: index + 1 })}
                    aria-required={true}
                    aria-invalid={typeIsInvalid}
                    aria-errormessage={typeIsInvalid ? errorElementId : undefined}
                    inputId={typeField.name}
                    name={typeField.name}
                    className={classNames("input-phone-number-type", styles.type)}
                    isSearchable={true}
                    value={typeField.value}
                    options={selectOptions}
                    onBlur={onTypeBlur}
                    onSelected={onTypeChange}
                    placeholder={formatMessage(messages.phoneNumberTypePlaceholderLabel)}
                    innerRef={typeField.ref}
                />
            </div>
            {
                !mask &&
                <Input
                    aria-label={formatMessage(messages.phoneNumberInputLabel, { index: index + 1 })}
                    aria-required={true}
                    aria-invalid={numberIsInvalid}
                    aria-errormessage={numberIsInvalid ? errorElementId : undefined}
                    className={classNames("form-control", styles.number)}
                    type="tel"
                    value={numberField.value}
                    onBlur={numberField.onBlur}
                    onChange={numberField.onChange}
                    innerRef={numberField.ref}
                    name={numberField.name}
                    invalid={numberIsInvalid}
                    valid={!numberIsInvalid && numberIsTouched}
                />
            }
            {
                mask &&
                <MaskedInput
                    aria-label={formatMessage(messages.phoneNumberInputLabel, { index: index + 1 })}
                    aria-required={true}
                    aria-invalid={numberIsInvalid}
                    aria-errormessage={numberIsInvalid ? errorElementId : undefined}
                    className={classNames(
                        "form-control",
                        styles.number,
                        {
                            "is-invalid": numberIsInvalid,
                            "is-valid": !numberIsInvalid && numberIsTouched,
                        }
                    )}
                    value={conformToMask(numberField.value, mask, {}).conformedValue}
                    mask={mask}
                    onBlur={numberField.onBlur}
                    onChange={numberField.onChange}
                    onClick={setMaskSelection}
                    onFocus={setMaskSelection}
                    name={numberField.name}
                    showMask={true}
                    placeholderChar={PLACEHOLDER_CHARACTER}
                    render={(maskRef, maskProps) => (
                        <input
                            type="tel"
                            {...maskProps}
                            ref={(inputNode) => {
                                if (inputNode) {
                                    maskRef(inputNode);
                                    numberField.ref(inputNode);
                                }
                            }}
                        />
                    )}
                />
            }
            {
                shouldRenderRemoveButton && onRemoveClicked &&
                <div className="input-group-append">
                    <Button
                        type="button"
                        onClick={onRemoveClicked}
                        title={formatMessage(messages.removePhoneNumberTooltip, { index: index + 1 })}>
                        <FontAwesomeIcon icon={faTrashAlt} className="icon-button" />
                    </Button>
                </div>
            }
            <FormFeedback id={errorElementId} className={!!error ? "d-block" : "d-none"} role="alert">{error?.message}</FormFeedback>
        </div>
    </div>;

    function onTypeBlur() {
        typeField.onBlur();
        trigger(formFieldArrayName);
    }

    function onTypeChange(option: OnChangeValue<ISelectOption, false>) {
        if (isSelectOption(option) && isPhoneNumberType(option.value)){
            typeField.onChange(option.value);
        } else {
            typeField.onChange(null);
        }

        onTypeBlur();
    }

    function setMaskSelection(event: React.MouseEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>) {
        const position = event.currentTarget.value.indexOf(PLACEHOLDER_CHARACTER);

        if (configuration && configuration.licensingPhoneNumberMask && position >= 0) {
            const valueSelected = event.currentTarget.value.substring(0, event.currentTarget.selectionEnd || undefined);

            const match = configuration.licensingPhoneNumberMask
                .find(m => !!(m.isRegex && valueSelected.match(new RegExp(m.value))));

            if (!match) {
                event.currentTarget.setSelectionRange(0, 0);
            } else if (event.currentTarget.selectionStart === null || event.currentTarget.selectionStart > position) {
                event.currentTarget.setSelectionRange(position, position);
            }
        }
    }
};

function getMask(configuration: PhoneNumber_configuration | null): Mask {
    if (!configuration ||
        !configuration.licensingPhoneNumberMask ||
        !configuration.licensingPhoneNumberMask.length) {
        return false;
    }

    return configuration.licensingPhoneNumberMask.map(maskCharacter => maskCharacter.isRegex ? new RegExp(maskCharacter.value) : maskCharacter.value);
}

const fragment = graphql`
    fragment PhoneNumber_configuration on Configuration {
        licensingPhoneNumberMask {
            value
            isRegex
        }
    }
`;
